import { Injectable } from 'angular-ts-decorators';

import { DateHelper } from '@core/app/scripts/services/date-helper';
import { Domain } from '@shared/app/scripts/services/domain';
import { SalesReports } from '@shared/app/scripts/services/sales-reports';
import type { IConfig } from '@shared/app/scripts/services/config';

/**
 * Service used for dashboard reports. Should be merged with SalesReports or Sales.
 */
@Injectable('salesDashboard')
export class SalesDashboard {
  private baseUrl: string;
  constructor(
    private $http: ng.IHttpService,
    private dateHelper: DateHelper,
    private salesReports: SalesReports,
    domain: Domain,
    config: IConfig,
  ) {
    'ngInject';

    this.baseUrl = '//' + domain.createSibling(config.spynlSubdomain);
  }

  /**
   * Method that choses proper group size and returns promise with the data
   * about tenant transactions during the period, organized by the shop.
   * @param {string} periodTypeValue
   * one of the values of {@link dashboardApp.periodType periodType}
   * @param {date} periodDate
   * date from anywhere inside period
   * @return {promise}
   * Same as returned by {@link sharedModule.salesReports#perWarehouse
   * salesReports#perWarehouse}
   */
  getPerWarehouse(periodTypeValue: string, periodDate: Date): ng.IPromise<any> {
    const periodQuery = this.dateHelper.getPeriodQuery(
        periodTypeValue,
        periodDate,
      ),
      startDate = periodQuery.gte,
      endDate = periodQuery.lt;

    return this.salesReports.perWarehouse(
      new Date(startDate),
      new Date(endDate),
    );
  }

  /**
   * Almost the same like {@link sharedModule.salesReport#getSummary
   * salesReports.getSummary}, but with periodTypeValue & periodDate parameters.
   * @param {string} periodTypeValue
   * one of the values of {@link dashboardApp.periodType periodType}
   * @param {date} periodDate
   * date from anywhere inside period
   * @param {integer|string} warehouseId
   * Id of the warehouse for which data should be requested
   * @return {promise} * Promise for the data being fetched
   */
  getPeriodSummary(
    periodTypeValue: string,
    periodDate: Date,
    warehouseId?: string,
  ): ng.IPromise<any> {
    const periodQuery = this.dateHelper.getPeriodQuery(
        periodTypeValue,
        periodDate,
      ),
      startDate = periodQuery.gte,
      endDate = periodQuery.lt;

    return this.salesReports.getSummary(
      new Date(startDate),
      new Date(endDate),
      warehouseId,
    );
  }

  /**
   * Method that returns the transactions summarized per each of period; plus
   * optionally filtered by the warehouse id.
   * Response structure:
   * <code><pre>
   * {
   *   date: {
   *     minute: 49,
   *     hour: 10,
   *     day: 12,
   *     month: 11, # this & all previous keys are hidden when 'group_by' is
   *                # longer; ie for group_by=day; minute & hour aren't provided
   *     year: 2015 # provided always
   *   },
   *   turnover: 166.95
   * }
   * </pre></code>
   * @param {date} startDate
   * End date of the period
   * @param {date} endDate
   * Start date of the period
   * @param {string} groupBy
   * Equal to one of "minute", "hour", "day", "month", "year", when provided
   * the transaction will be grouped by the chosen unit
   * @param {integer|string} warehouseId
   * Id of the warehouse for which data should be requested
   * @return {promise}
   * Promise for the data being fetched
   */
  getPeriod(
    startDate: Date | string,
    endDate: Date | string,
    groupBy: string,
    warehouseId?: string, // TODO define & validate with data schema
  ): ng.IPromise<any> {
    return this.$http
      .get(this.baseUrl + '/sales/period', {
        params: {
          startDate,
          endDate,
          warehouseId,
          group_by: groupBy,
        },
      })
      .then((response: any) => response.data.data);
  }

  /**
   * Almost the same like {@link dashboardApp.sales#getStandardPeriod
   * getStandardPeriod}, but with periodTypeValue & periodDate parameters.
   * @param {string} periodTypeValue
   * one of the values of {@link dashboardApp.periodType periodType}
   * @param {date} periodDate
   * date from anywhere inside period
   * @param {string} groupBy
   * Equal to one of "minute", "hour", "day", "month", "year", when provided
   * the transaction will be grouped by the chosen unit
   * @param {integer|string} warehouseId
   * Id of the warehouse for which data should be requested
   * @return {promise}
   * Promise for the data being fetched
   */
  getStandardPeriod(
    periodTypeValue: string,
    periodDate: Date,
    groupBy: string,
    warehouseId?: string,
  ): ng.IPromise<any> {
    const periodQuery = this.dateHelper.getPeriodQuery(
        periodTypeValue,
        periodDate,
      ),
      startDate = periodQuery.gte,
      endDate = periodQuery.lt;

    return this.getPeriod(startDate, endDate, groupBy, warehouseId);
  }
}
